import React from 'react';
import ReactDOM from 'react-dom';
import { DataProvider, useData, useField } from './EpcrDataContext';
import { Icons } from './EpcrIcons';
import { EpcrViewIncident } from './views/EpcrViewIncident';
import { EpcrViewPatient } from './views/EpcrViewPatient';
import { EpcrViewVitals } from './views/EpcrViewVitals';
import { EpcrViewFlowchart } from './views/EpcrViewFlowchart';
import { EpcrViewAssessments } from './views/EpcrViewAssessments';
import { EpcrViewNarrative } from './views/EpcrViewNarrative';
import { EpcrViewBilling, EpcrViewSignatures } from './views/EpcrViewRest';
import { EpcrViewRaw, RawField, groupLabel } from './views/EpcrViewRaw';
import { AutoField } from './EpcrFields';
import './EpcrPage.css';

const URLS = require('../../../urls.js');

const VIEWS = [
  { id: "incident",    label: "Incident",    icon: Icons.truck },
  { id: "patient",     label: "Patient",     icon: Icons.user },
  { id: "vitals",      label: "Vitals",      icon: Icons.pulse },
  { id: "flowchart",   label: "Flowchart",   icon: Icons.flow },
  { id: "assessments", label: "Assessments", icon: Icons.clipboard },
  { id: "narrative",   label: "Narrative",   icon: Icons.note },
  { id: "billing",     label: "Billing",     icon: Icons.dollar },
  { id: "signatures",  label: "Signatures",  icon: Icons.pen },
  { id: "raw",         label: "Raw",         icon: Icons.list },
];

const VIEW_COMPONENTS = {
  incident:    EpcrViewIncident,
  patient:     EpcrViewPatient,
  vitals:      EpcrViewVitals,
  flowchart:   EpcrViewFlowchart,
  assessments: EpcrViewAssessments,
  narrative:   EpcrViewNarrative,
  billing:     EpcrViewBilling,
  signatures:  EpcrViewSignatures,
  raw:         EpcrViewRaw,
};

// Maps NEMSIS element numbers to the view and section that contain them.
// Prefix-based fallback handles any element not explicitly listed.
const FIELD_MAP = {
  // incident — response
  'eRecord.01':    { view: 'incident', section: 'response' },
  'eResponse.03':  { view: 'incident', section: 'response' },
  'eResponse.04':  { view: 'incident', section: 'response' },
  'eResponse.05':  { view: 'incident', section: 'response' },
  'eResponse.07':  { view: 'incident', section: 'response' },
  'eResponse.08':  { view: 'incident', section: 'response' },
  'eResponse.09':  { view: 'incident', section: 'response' },
  'eResponse.13':  { view: 'incident', section: 'response' },
  'eResponse.14':  { view: 'incident', section: 'response' },
  'eResponse.23':  { view: 'incident', section: 'response' },
  'eResponse.24':  { view: 'incident', section: 'response' },
  // incident — scene
  'eScene.06':     { view: 'incident', section: 'scene' },
  'eScene.07':     { view: 'incident', section: 'scene' },
  'eScene.09':     { view: 'incident', section: 'scene' },
  'eScene.11':     { view: 'incident', section: 'scene' },
  'eScene.15':     { view: 'incident', section: 'scene' },
  'eScene.17':     { view: 'incident', section: 'scene' },
  'eScene.18':     { view: 'incident', section: 'scene' },
  'eScene.19':     { view: 'incident', section: 'scene' },
  'eScene.21':     { view: 'incident', section: 'scene' },
  // incident — times
  'eTimes.01':     { view: 'incident', section: 'times' },
  'eTimes.02':     { view: 'incident', section: 'times' },
  'eTimes.03':     { view: 'incident', section: 'times' },
  'eTimes.05':     { view: 'incident', section: 'times' },
  'eTimes.06':     { view: 'incident', section: 'times' },
  'eTimes.07':     { view: 'incident', section: 'times' },
  'eTimes.09':     { view: 'incident', section: 'times' },
  'eTimes.11':     { view: 'incident', section: 'times' },
  'eTimes.13':     { view: 'incident', section: 'times' },
  // incident — disposition
  'eDisposition.27': { view: 'incident', section: 'disposition' },
  'eDisposition.13': { view: 'incident', section: 'disposition' },
  'eDisposition.14': { view: 'incident', section: 'disposition' },
  'eDisposition.16': { view: 'incident', section: 'disposition' },
  'eDisposition.17': { view: 'incident', section: 'disposition' },
  'eDisposition.19': { view: 'incident', section: 'disposition' },
  'eDisposition.20': { view: 'incident', section: 'destination' },
  'eDisposition.21': { view: 'incident', section: 'destination' },
  'eDisposition.28': { view: 'incident', section: 'disposition' },
  'eDisposition.29': { view: 'incident', section: 'disposition' },
  'eDisposition.30': { view: 'incident', section: 'disposition' },
  'eDisposition.31': { view: 'incident', section: 'disposition' },
  'eDisposition.32': { view: 'incident', section: 'disposition' },
  // patient — demographics
  'ePatient.02':   { view: 'patient', section: 'demographics' },
  'ePatient.03':   { view: 'patient', section: 'demographics' },
  'ePatient.04':   { view: 'patient', section: 'demographics' },
  'ePatient.12':   { view: 'patient', section: 'demographics' },
  'ePatient.14':   { view: 'patient', section: 'demographics' },
  'ePatient.15':   { view: 'patient', section: 'demographics' },
  'ePatient.16':   { view: 'patient', section: 'demographics' },
  'ePatient.17':   { view: 'patient', section: 'demographics' },
  'ePatient.24':   { view: 'patient', section: 'demographics' },
  'ePatient.25':   { view: 'patient', section: 'demographics' },
  // patient — contact
  'ePatient.05':   { view: 'patient', section: 'contact' },
  'ePatient.06':   { view: 'patient', section: 'contact' },
  'ePatient.07':   { view: 'patient', section: 'contact' },
  'ePatient.08':   { view: 'patient', section: 'contact' },
  'ePatient.09':   { view: 'patient', section: 'contact' },
  'ePatient.10':   { view: 'patient', section: 'contact' },
  'ePatient.18':   { view: 'patient', section: 'contact' },
  'ePatient.19':   { view: 'patient', section: 'contact' },
  // patient — history / allergies
  'eHistory.06':   { view: 'patient', section: 'allergies' },
  'eHistory.07':   { view: 'patient', section: 'allergies' },
  'eHistory.08':   { view: 'patient', section: 'history' },
  'eHistory.13':   { view: 'patient', section: 'history' },
  // narrative
  'eSituation.01': { view: 'narrative', section: 'times' },
  'eSituation.03': { view: 'narrative', section: 'complaint' },
  'eSituation.04': { view: 'narrative', section: 'complaint' },
  'eSituation.05': { view: 'narrative', section: 'complaint' },
  'eSituation.06': { view: 'narrative', section: 'complaint' },
  'eSituation.07': { view: 'narrative', section: 'complaint' },
  'eSituation.09': { view: 'narrative', section: 'impression' },
  'eSituation.10': { view: 'narrative', section: 'signs' },
  'eSituation.11': { view: 'narrative', section: 'impression' },
  'eSituation.12': { view: 'narrative', section: 'impression' },
  'eSituation.18': { view: 'narrative', section: 'times' },
  'eNarrative.01': { view: 'narrative', section: 'narrative' },
  'eInjury.01':    { view: 'narrative', section: 'injuries' },
  'eInjury.03':    { view: 'narrative', section: 'injuries' },
};

const VIEW_BY_PREFIX = {
  eRecord: 'incident', eResponse: 'incident', eTimes: 'incident',
  eScene: 'incident', eDisposition: 'incident', eCrew: 'incident',
  ePatient: 'patient', eHistory: 'patient',
  eVitals: 'vitals',
  eProcedures: 'flowchart', eMedications: 'flowchart',
  eSituation: 'narrative', eNarrative: 'narrative', eInjury: 'narrative',
  eExam: 'assessments',
};

function getFieldLocation(elemNumber) {
  if (!elemNumber) return null;
  if (FIELD_MAP[elemNumber]) return FIELD_MAP[elemNumber];
  const prefix = elemNumber.split('.')[0];
  const view = VIEW_BY_PREFIX[prefix];
  return view ? { view, section: null } : null;
}

const REPORTS = [
  { file: "report_empty.json",       label: "2026-demo-01",   sublabel: "Blank Report" },
  { file: "report.json",             label: "2026-demo-02",   sublabel: "Seizure" },
];

// Hardware-device voice note recordings available to attach to a CADMUS send.
// Fetched on demand from ReactApp/public/epcr-data/voice_note_files/.
const VOICE_NOTE_FILES = ["voice_notes_seizure.json", "voice_notes_arrest.json"];

export default function EpcrPage() {
  return (
    <DataProvider>
      <EpcrApp />
    </DataProvider>
  );
}

// ── 7A helpers: path resolution and op application ──────────────────────────

function mutationKey(r) {
  return `${r.feedbackTarget}:${r.elem}:${r.op}:${r.value}:${r.test_metadata_id}:${r.groupIndex ?? 'x'}`;
}

function promoteToMutation(proto, data) {
  const path = (proto.elem ? data.path(proto.elem) : null) || null;
  const groupPath = (path && path.includes('Group'))
    ? path.split('/').slice(0, -1).join('/')
    : null;

  // If the LLM supplied a groupIndex, inject it after the Group segment so the
  // write targets the correct array item rather than defaulting to [0].
  let resolvedPath = path;
  if (path && groupPath && Number.isInteger(proto.groupIndex) && proto.groupIndex >= 0) {
    const groupArr = readAtPath(data.report, groupPath);
    let idx = 0;
    if (Array.isArray(groupArr)) {
      if (proto.groupIndex < groupArr.length) {
        idx = proto.groupIndex;
      } else {
        console.warn(`[promoteToMutation] groupIndex=${proto.groupIndex} out of bounds (len=${groupArr.length}), falling back to 0`);
      }
    }
    const segs = path.split('/');
    const gi = segs.findIndex(s => s.includes('Group'));
    if (gi !== -1) {
      segs.splice(gi + 1, 0, String(idx));
      resolvedPath = segs.join('/');
    }
  }

  return { ...proto, path: resolvedPath, groupPath };
}

function readAtPath(report, path) {
  try {
    let obj = report.EMSDataSet.Header.PatientCareReport;
    const segs = path.split('/');
    for (let i = 0; i < segs.length; i++) {
      if (obj == null) return undefined;
      obj = obj[segs[i]];
      // Dereference repeating group arrays on intermediate segments only.
      // Skip when the next segment is already a numeric index (Raw view paths).
      // The final segment is left as-is so add_group receives the full array.
      if (i < segs.length - 1 && Array.isArray(obj) && !/^\d+$/.test(segs[i + 1])) obj = obj[0];
    }
    return obj;
  } catch { return undefined; }
}

function applyMutation(proto, data) {
  if (!proto.elem || !proto.op) return false;
  const m = promoteToMutation(proto, data);
  if (!m.path) {
    console.warn(`[applyMutation] no path for elem=${proto.elem}`);
    return false;
  }
  const { op, value, path, groupPath } = m;
  const current = readAtPath(data.report, path);

  switch (op) {
    case 'set':
    case 'clear':
      data.setReportPath(path, value ?? null);
      break;
    case 'replace':
      if (Array.isArray(current)) {
        data.setReportPath(path, current.map(v => v === proto.removeValue ? value : v));
      } else {
        data.setReportPath(path, value);
      }
      break;
    case 'add': {
      const arr = Array.isArray(current) ? current : (current != null ? [current] : []);
      data.setReportPath(path, [...arr, value]);
      break;
    }
    case 'add_group':
    case 'add_group_full': {
      // add_group_full carries a richer, multi-field proto.template (identity + anchor
      // time + whatever extra fields were determined — see MutationCard), but the write
      // itself is identical: append the template (or the single-field fallback) to the
      // group array.
      if (!groupPath) { console.warn(`[applyMutation] ${op} but no groupPath`); return false; }
      const groupCurrent = readAtPath(data.report, groupPath);
      const arr = Array.isArray(groupCurrent) ? groupCurrent : (groupCurrent != null ? [groupCurrent] : []);
      const newGroup = proto.template || (value != null ? { [proto.elem]: value } : {});
      data.setReportPath(groupPath, [...arr, newGroup]);
      break;
    }
    case 'ask_user':
      // Value is written live by RawField as the user fills in the field directly
      // inside the Suggested Edits card (see MutationCard) — nothing left to apply.
      break;
    default:
      console.warn(`[applyMutation] unknown op=${op}`);
      return false;
  }
  console.log(`[applyMutation] op=${op} elem=${proto.elem} path=${path} value=${value}`);
  return true;
}

function EpcrApp() {
  const [view, setView]                 = React.useState("incident");
  const [rapidOpen, setRapidOpen]       = React.useState(false);
  const [reportOpen, setReportOpen]     = React.useState(false);
  const [validationOpen, setValidationOpen] = React.useState(false);
  const [validating, setValidating]     = React.useState(false);
  const [violations, setViolations]     = React.useState(null);
  const [sending, setSending]                         = React.useState(false);
  const [dropdownResults, setDropdownResults]         = React.useState(null);
  const [schematronMutations, setSchematronMutations] = React.useState(null);
  const [rejectedIds, setRejectedIds]                 = React.useState([]);
  const [acceptedIds, setAcceptedIds]                 = React.useState([]);
  const [voiceNoteCache, setVoiceNoteCache]           = React.useState({});
  const [attachedNotes, setAttachedNotes]             = React.useState([]);
  const [appNoteBatches, setAppNoteBatches]           = React.useState([]);
  const data = useData();

  const handleSelectReport = React.useCallback(async (file) => {
    setReportOpen(false);
    setValidationOpen(false);
    setViolations(null);
    setValidating(false);
    setSending(false);
    setDropdownResults(null);
    setSchematronMutations(null);
    setRejectedIds([]);
    setAcceptedIds([]);
    setAttachedNotes([]);
    await data.loadReport(file);
  }, [data]);

  const loadVoiceNoteFile = React.useCallback(async (file) => {
    if (voiceNoteCache[file]) return voiceNoteCache[file];
    try {
      const json = await fetch(`/epcr-data/voice_note_files/${file}`).then(r => r.json());
      const recordings = json.recordings || [];
      setVoiceNoteCache(c => ({ ...c, [file]: recordings }));
      return recordings;
    } catch (e) {
      console.error('Voice note file load error:', e);
      setVoiceNoteCache(c => ({ ...c, [file]: [] }));
      return [];
    }
  }, [voiceNoteCache]);

  const toggleAttachNote = React.useCallback((note, file) => {
    const uid = `${file}:${note.id}`;
    setAttachedNotes(prev => {
      const exists = prev.some(n => n.uid === uid);
      if (exists) return prev.filter(n => n.uid !== uid);
      return [...prev, { uid, text: note.text, timestamp: note.timestamp, source: file }];
    });
  }, []);

  const attachAllFromFile = React.useCallback((file, recordings) => {
    setAttachedNotes(prev => {
      const existingUids = new Set(prev.map(n => n.uid));
      const toAdd = (recordings || [])
        .filter(rec => !existingUids.has(`${file}:${rec.id}`))
        .map(rec => ({ uid: `${file}:${rec.id}`, text: rec.text, timestamp: rec.timestamp, source: file }));
      return toAdd.length ? [...prev, ...toAdd] : prev;
    });
  }, []);

  const addManualNote = React.useCallback((text, timestamp) => {
    const uid = `manual:${Date.now()}:${Math.random().toString(36).slice(2, 8)}`;
    setAttachedNotes(prev => [...prev, { uid, text, timestamp, source: 'manual' }]);
  }, []);

  // Polls the batches the mvp3 Android app has POSTed to /api/admin/receive_notes_from_app
  // (in-memory on the backend, not persisted). Called on mount of VoiceNotesPanel and on an
  // interval while that tab is visible, plus a manual "Refresh" button — see VoiceNotesPanel.
  const refreshAppNotes = React.useCallback(async () => {
    try {
      const resp = await fetch(URLS.LIST_NOTES_FROM_APP, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      const result = await resp.json();
      setAppNoteBatches(result.batches || []);
    } catch (e) {
      console.error('List notes from app error:', e);
    }
  }, []);

  const removeAttachedNote = React.useCallback((uid) => {
    setAttachedNotes(prev => prev.filter(n => n.uid !== uid));
  }, []);

  const clearAttachedNotes = React.useCallback(() => {
    setAttachedNotes([]);
  }, []);

  const runValidation = React.useCallback(async () => {
    if (!data?.report) return;
    setValidating(true);
    setValidationOpen(true);
    try {
      const resp = await fetch(URLS.VALIDATE_EPCR_DRAFT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ json: data.report }),
      });
      const result = await resp.json();
      setViolations(result.violations || []);
    } catch (e) {
      console.error('Validation error:', e);
      setViolations([]);
    } finally {
      setValidating(false);
    }
  }, [data]);

  const runSendToCadmus = React.useCallback(async () => {
    if (!data?.report) return;
    setSending(true);
    setValidationOpen(true);
    try {
      // Sequential per-send id — this is what the backend hands to the LLM as a
      // timestamp proxy (see validateClinicalDomainsWithNarrativeAndVoiceNotes),
      // so the LLM never sees the real timestamp, only the id and text.
      const notes = attachedNotes.map((n, i) => ({ id: i + 1, timestamp: n.timestamp, text: n.text }));
      const resp = await fetch(URLS.SEND_TO_CADMUS, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ json: data.report, notes }),
      });
      const result = await resp.json();
      setViolations(result.violations || []);
      setDropdownResults(result.dropdownResults || []);
      setSchematronMutations(result.schematronMutations || []);
    } catch (e) {
      console.error('Send to CADMUS error:', e);
      setDropdownResults([]);
      setSchematronMutations([]);
    } finally {
      setSending(false);
    }
  }, [data, attachedNotes]);

  const handleAccept = React.useCallback((r) => {
    applyMutation(r, data);
    setAcceptedIds(ids => [...ids, mutationKey(r)]);
  }, [data]);

  const handleReject = React.useCallback((r) => {
    setRejectedIds(ids => [...ids, mutationKey(r)]);
  }, []);

  const handleAcceptAll = React.useCallback(() => {
    const all = [
      ...(schematronMutations || []).filter(r => r.severity !== '[FATAL]'),
      ...(dropdownResults || []),
    ];
    const rejectedSet = new Set(rejectedIds);
    const toApply = all.filter(r => r.op !== null && r.op !== 'ask_user' && !rejectedSet.has(mutationKey(r)));
    const newlyAccepted = [];
    for (const r of toApply) {
      applyMutation(r, data);
      newlyAccepted.push(mutationKey(r));
    }
    if (newlyAccepted.length > 0) {
      setAcceptedIds(ids => [...ids, ...newlyAccepted]);
    }
  }, [schematronMutations, dropdownResults, rejectedIds, data]);

  const navigateToViolation = React.useCallback((violation) => {
    // For schematron violations, contextLocalName is the exact XML element that fired —
    // derive the tab from its prefix. Fall back to static FIELD_MAP for clinical mutations.
    const view = (() => {
      if (violation.contextLocalName) {
        const prefix = violation.contextLocalName.split('.')[0];
        return VIEW_BY_PREFIX[prefix] || null;
      }
      return getFieldLocation(violation.element)?.view || null;
    })();
    if (view) setView(view);
    setValidationOpen(false);
    const loc = getFieldLocation(violation.element);
    setTimeout(() => {
      const flash = (el) => {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        el.classList.add('epcr-jump-flash');
        setTimeout(() => el.classList.remove('epcr-jump-flash'), 1500);
      };
      // Prefer card-level targeting when the violation carries DOM-derived group info.
      const groupPrefix = violation.contextLocalName?.split('.')[0];
      const groupIndex  = violation.contextGroupIndex;
      const cardEl = (groupPrefix != null && groupIndex != null)
        ? document.querySelector(`[data-group-prefix="${groupPrefix}"][data-group-index="${groupIndex}"]`)
        : null;
      if (cardEl) {
        flash(cardEl);
      } else {
        const fieldEl = document.getElementById(`field-${violation.element}`);
        if (fieldEl) {
          flash(fieldEl);
        } else if (loc?.section) {
          const sectionEl = document.querySelector(`[data-section="${loc.section}"]`);
          if (sectionEl) sectionEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }
    }, 80);
  }, []);

  if (!data?.loaded) {
    return (
      <div className="epcr-root" style={{ display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", gap: 14, color: "var(--epcr-ink-3)" }}>
        <div className="loader-brand"><span className="r">CADMUS</span> ePCR</div>
        <div className="loader-bar" />
        <div style={{ fontSize: 12 }}>Loading enumerations & defined lists…</div>
      </div>
    );
  }

  const ViewComponent = VIEW_COMPONENTS[view];
  const totalCount = violations ? violations.length : 0;

  return (
    <div className="epcr-root">
      <TopToolbar
        onRapid={() => setRapidOpen(o => !o)}
        rapidOpen={rapidOpen}
        onCloseRapid={() => setRapidOpen(false)}
        onValidate={() => validationOpen ? setValidationOpen(false) : (violations !== null ? setValidationOpen(true) : runValidation())}
        validating={validating}
        violationCount={totalCount}
        hasViolations={violations !== null}
        reports={REPORTS}
        currentReport={data.currentReport}
        reportOpen={reportOpen}
        onToggleReport={() => setReportOpen(o => !o)}
        onCloseReport={() => setReportOpen(false)}
        onSelectReport={handleSelectReport}
      />
      <ViewTabs current={view} onSelect={setView} />
      <ViewComponent />
      {validationOpen && (
        <ValidationPanel
          violations={violations}
          validating={validating}
          totalCount={totalCount}
          onClose={() => setValidationOpen(false)}
          onRerun={runValidation}
          onNavigate={navigateToViolation}
          specFn={data.spec}
          sending={sending}
          dropdownResults={dropdownResults}
          schematronMutations={schematronMutations}
          rejectedIds={rejectedIds}
          acceptedIds={acceptedIds}
          onAccept={handleAccept}
          onReject={handleReject}
          onAcceptAll={handleAcceptAll}
          onSendToCadmus={runSendToCadmus}
          voiceNoteFiles={VOICE_NOTE_FILES}
          voiceNoteCache={voiceNoteCache}
          onLoadVoiceNoteFile={loadVoiceNoteFile}
          attachedNotes={attachedNotes}
          onToggleAttachNote={toggleAttachNote}
          onAttachAllFromFile={attachAllFromFile}
          onAddManualNote={addManualNote}
          onRemoveAttachedNote={removeAttachedNote}
          onClearAttachedNotes={clearAttachedNotes}
          appNoteBatches={appNoteBatches}
          onRefreshAppNotes={refreshAppNotes}
        />
      )}
    </div>
  );
}

function TopToolbar({ onRapid, rapidOpen, onCloseRapid, onValidate, validating, violationCount, hasViolations,
                      reports, currentReport, reportOpen, onToggleReport, onCloseReport, onSelectReport }) {
  const last = useField("ePatient/ePatient.PatientNameGroup/ePatient.02");
  const first = useField("ePatient/ePatient.PatientNameGroup/ePatient.03");
  const runNumber = useField("eResponse/eResponse.04");
  const incidentNumber = useField("eResponse/eResponse.03");
  const patientName = [first, last].filter(Boolean).join(" ") || "Unnamed Patient";
  const runLabel = runNumber || incidentNumber || "";

  return (
    <div className="t1">
      <div className="t1-brand">
        <span className="dot" />
        Example ePCR
      </div>
      <button className="t1-home"><Icons.home size={36} /></button>
      <div style={{ position: "relative" }}>
        <button className="t1-patient" onClick={onToggleReport}>
          <span className="label">{runLabel ? "Run " + runLabel : ""}</span>
          <span className="name">
            {patientName} <Icons.chev className="chev" size={28} />
          </span>
        </button>
        {reportOpen && (
          <>
            <div style={{ position: "fixed", inset: 0, zIndex: 49 }} onClick={onCloseReport} />
            <div className="report-pop">
              <h4>Switch Report</h4>
              {reports.map(r => (
                <button
                  key={r.file}
                  className={r.file === currentReport ? "active" : ""}
                  onClick={() => onSelectReport(r.file)}
                >
                  <span className="report-pop-label">{r.label}</span>
                  <span className="report-pop-sub">{r.sublabel}</span>
                </button>
              ))}
            </div>
          </>
        )}
      </div>
      {/* <button className="t1-btn" title="Switch report"><Icons.swap size={32} /></button> */}
      <div className="t1-spacer" />
      <div className="t1-actions" style={{ position: "relative" }}>
        {/* <button className="t1-btn" title="Keyboard shortcuts"><Icons.keyboard size={36} /></button> */}
        <div className="t1-btn-wrap">
          <button
            className={"t1-btn" + (validating ? " rapid" : "")}
            title="Validate"
            onClick={onValidate}
          >
            {validating ? <Icons.star4 size={36} /> : <Icons.star4 size={36} />}
          </button>
          {hasViolations && violationCount > 0 && (
            <span className="t1-vbadge">{violationCount > 99 ? '99+' : violationCount}</span>
          )}
        </div>
        <button className="t1-btn" title="Timeline"><Icons.timeline size={36} /></button>
        {/* <button className="t1-btn" title="Capture image"><Icons.camera size={36} /></button> */}
        <button className="t1-btn" title="Menu"><Icons.menu size={36} /></button>
        {/* <button className="t1-btn rapid" title="Rapid actions" onClick={onRapid}>
          <Icons.bolt size={36} />
        </button> */}
        {rapidOpen && (
          <>
            <div style={{ position: "fixed", inset: 0, zIndex: 49 }} onClick={onCloseRapid} />
            <div className="rapid-pop">
              <h4>Rapid actions</h4>
              <button><Icons.pulse size={28} /> Add vitals</button>
              <button><Icons.flow size={28} /> Repeat last treatment</button>
              <button><Icons.clipboard size={28} /> Reassessment template</button>
              <button><Icons.note size={28} /> Append to narrative</button>
              <button><Icons.alert size={28} /> Flag critical</button>
              <button className="danger"><Icons.alert size={28} /> Mark refused care</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function ViewTabs({ current, onSelect }) {
  return (
    <nav className="t2" aria-label="Report sections">
      {VIEWS.map(v => (
        <button
          key={v.id}
          className={"t2-tab" + (current === v.id ? " active" : "")}
          onClick={() => onSelect(v.id)}
        >
          <v.icon size={36} className="icon" />
          <span>{v.label}</span>
        </button>
      ))}
    </nav>
  );
}

function ValidationPanel({ violations, validating, totalCount, onClose, onRerun, onNavigate, specFn,
                            sending, dropdownResults, schematronMutations,
                            rejectedIds, acceptedIds, onAccept, onReject, onAcceptAll, onSendToCadmus,
                            voiceNoteFiles, voiceNoteCache, onLoadVoiceNoteFile,
                            attachedNotes, onToggleAttachNote, onAttachAllFromFile,
                            onAddManualNote, onRemoveAttachedNote, onClearAttachedNotes,
                            appNoteBatches, onRefreshAppNotes }) {
  const [tab, setTab] = React.useState('schematron');
  const fatal   = (violations || []).filter(v => v.role === '[FATAL]');
  const warning = (violations || []).filter(v => v.role !== '[FATAL]');

  const rejectedSet = React.useMemo(() => new Set(rejectedIds || []), [rejectedIds]);
  const acceptedSet = React.useMemo(() => new Set(acceptedIds || []), [acceptedIds]);
  const visibleSchematron = React.useMemo(
    () => (schematronMutations || []).filter(r => !rejectedSet.has(mutationKey(r))),
    [schematronMutations, rejectedSet]
  );
  const visibleClinical = React.useMemo(
    () => (dropdownResults || []).filter(r => !rejectedSet.has(mutationKey(r))),
    [dropdownResults, rejectedSet]
  );
  const actionableCount = React.useMemo(
    () => [...visibleSchematron, ...visibleClinical]
            .filter(r => r.op !== null && r.op !== 'ask_user' && r.severity !== '[FATAL]').length,
    [visibleSchematron, visibleClinical]
  );

  return ReactDOM.createPortal(
    <div className="epcr-vp-back" onClick={onClose}>
      <div className="epcr-vp" onClick={e => e.stopPropagation()}>
        <div className="epcr-vp-head">
          <img src="/logo-dark.png" alt="CADMUS" style={{ height: 28, display: "block" }} />
          {violations !== null && !validating && !sending && (
            <span className={"epcr-vp-count" + (totalCount === 0 ? " ok" : "")}>
              {totalCount === 0 ? "All clear" : `${totalCount} issue${totalCount !== 1 ? 's' : ''}`}
            </span>
          )}
          <div className="epcr-vp-actions">
            <button className="epcr-vp-rerun" onClick={e => { e.stopPropagation(); onRerun(); }} disabled={validating || sending}>
              {validating ? "Running…" : "Re-run"}
            </button>
            <button
              className="epcr-vp-send"
              onClick={e => { e.stopPropagation(); onSendToCadmus(); setTab('mutations'); }}
              disabled={validating || sending}
            >
              {sending ? "Sending…" : "Send to CADMUS"}
            </button>
          </div>
          <button className="epcr-vp-close" onClick={onClose}>
            <Icons.close size={32} />
          </button>
        </div>

        <div className="epcr-vp-tabs">
          <button className={"epcr-vp-tab" + (tab === 'schematron' ? " active" : "")} onClick={() => setTab('schematron')}>
            Schematron
          </button>
          {/* <button className={"epcr-vp-tab" + (tab === 'raw' ? " active" : "")} onClick={() => setTab('raw')}>
            Raw
          </button> */}
          <button className={"epcr-vp-tab" + (tab === 'mutations' ? " active" : "")} onClick={() => setTab('mutations')}>
            Suggested Edits
            {(() => {
              const total = (dropdownResults || []).length + (schematronMutations || []).length;
              return (dropdownResults !== null || schematronMutations !== null) && total > 0
                ? <span className="epcr-vp-tab-count">{total}</span>
                : null;
            })()}
          </button>
          <button className={"epcr-vp-tab" + (tab === 'voicenotes' ? " active" : "")} onClick={() => setTab('voicenotes')}>
            Voice Notes
            {attachedNotes.length > 0 && (
              <span className="epcr-vp-tab-count">{attachedNotes.length}</span>
            )}
          </button>
          <button className={"epcr-vp-tab" + (tab === 'debug' ? " active" : "")} onClick={() => setTab('debug')}>
            Debug
          </button>
        </div>

        <div className="epcr-vp-list">
          {tab === 'schematron' && (
            <>
              {validating && <div className="epcr-vp-empty">Running schematron validation…</div>}
              {!validating && violations === null && (
                <div className="epcr-vp-empty">Click Re-run to validate this report against the schematron rules.</div>
              )}
              {!validating && violations !== null && totalCount === 0 && (
                <div className="epcr-vp-empty">No violations found.</div>
              )}
              {!validating && fatal.length > 0 && (
                <>
                  <div className="epcr-vp-group-head">Fatal ({fatal.length})</div>
                  {fatal.map((v, i) => (
                    <ViolationItem key={i} v={v} onNavigate={v => { onNavigate(v); onClose(); }} specFn={specFn} />
                  ))}
                </>
              )}
              {!validating && warning.length > 0 && (
                <>
                  <div className="epcr-vp-group-head">Warning ({warning.length})</div>
                  {warning.map((v, i) => (
                    <ViolationItem key={i} v={v} onNavigate={v => { onNavigate(v); onClose(); }} specFn={specFn} />
                  ))}
                </>
              )}
            </>
          )}

          {tab === 'raw' && (
            <>
              {sending && <div className="epcr-vp-empty">Sending to CADMUS — running full analysis…</div>}
              {!sending && dropdownResults === null && (
                <div className="epcr-vp-empty">Click "Send to CADMUS" to run full clinical analysis.</div>
              )}
              {!sending && dropdownResults !== null && (
                <>
                  {violations !== null && violations.length > 0 && (
                    <>
                      <div className="epcr-vp-group-head">Schematron ({violations.length})</div>
                      {violations.map((v, i) => (
                        <div key={i} className="epcr-vp-item" style={{ cursor: 'default' }}>
                          <div className="epcr-vp-item-meta">
                            <span className={"epcr-vp-badge " + (v.role === '[FATAL]' ? "fatal" : "warn")}>
                              {v.role === '[FATAL]' ? "Fatal" : "Warning"}
                            </span>
                            {v.element && <span className="epcr-vp-elem">{v.element}</span>}
                          </div>
                          <div className="epcr-vp-msg">{v.message}</div>
                        </div>
                      ))}
                    </>
                  )}
                  {violations !== null && violations.length === 0 && (
                    <div className="epcr-vp-group-head" style={{ color: '#16a34a' }}>Schematron — No violations</div>
                  )}
                  <div className="epcr-vp-group-head">AI Analysis ({dropdownResults.length})</div>
                  {dropdownResults.length === 0 && (
                    <div className="epcr-vp-empty" style={{ padding: '20px 36px' }}>No discrepancies found.</div>
                  )}
                  {dropdownResults.map((r, i) => (
                    <DropdownResultItem key={i} r={r} />
                  ))}
                </>
              )}
            </>
          )}

          {tab === 'mutations' && (
            <>
              {sending && <div className="epcr-vp-empty">Sending to CADMUS — running analysis…</div>}
              {!sending && dropdownResults === null && schematronMutations === null && (
                <div className="epcr-vp-empty">Click "Send to CADMUS" to generate mutations.</div>
              )}
              {!sending && (dropdownResults !== null || schematronMutations !== null) && (
                <>
                  {actionableCount > 0 && (
                    <div className="epcr-mut-bulk">
                      <span>{actionableCount} actionable edit{actionableCount !== 1 ? 's' : ''}</span>
                      <button className="epcr-mut-btn accept" onClick={onAcceptAll}>
                        Accept all edits
                      </button>
                    </div>
                  )}
                  {visibleSchematron.length > 0 && (
                    <>
                      <div className="epcr-vp-group-head">Schematron Mutations ({visibleSchematron.length})</div>
                      {visibleSchematron.map((r, i) => (
                        <MutationCard key={`sch-${i}`} r={r} isAccepted={acceptedSet.has(mutationKey(r))} onAccept={onAccept} onReject={onReject} />
                      ))}
                    </>
                  )}
                  {visibleClinical.length > 0 && (
                    <>
                      <div className="epcr-vp-group-head">Clinical Analysis ({visibleClinical.length})</div>
                      {visibleClinical.map((r, i) => (
                        <MutationCard key={`clin-${i}`} r={r} isAccepted={acceptedSet.has(mutationKey(r))} onAccept={onAccept} onReject={onReject} />
                      ))}
                    </>
                  )}
                  {visibleSchematron.length === 0 && visibleClinical.length === 0 && (
                    <div className="epcr-vp-empty">No mutations identified.</div>
                  )}
                </>
              )}
            </>
          )}

          {tab === 'voicenotes' && (
            <VoiceNotesPanel
              files={voiceNoteFiles}
              cache={voiceNoteCache}
              onLoadFile={onLoadVoiceNoteFile}
              attachedNotes={attachedNotes}
              onToggleAttach={onToggleAttachNote}
              onAttachAllFromFile={onAttachAllFromFile}
              onAddManual={onAddManualNote}
              onRemoveAttached={onRemoveAttachedNote}
              onClearAttached={onClearAttachedNotes}
              appBatches={appNoteBatches}
              onRefreshAppNotes={onRefreshAppNotes}
            />
          )}

          {tab === 'debug' && (() => {
            const currentKeys = [
              ...(schematronMutations || []),
              ...(dropdownResults || []),
            ].map(r => mutationKey(r));
            const sections = [
              { label: 'Current mutation keys', items: currentKeys },
              { label: 'acceptedIds', items: acceptedIds || [] },
              { label: 'rejectedIds', items: rejectedIds || [] },
            ];
            return (
              <div style={{ padding: '16px 20px', fontFamily: 'monospace', fontSize: 12 }}>
                {sections.map(({ label, items }) => (
                  <div key={label} style={{ marginBottom: 20 }}>
                    <div style={{ fontWeight: 700, marginBottom: 6, fontSize: 13 }}>{label} ({items.length})</div>
                    {items.length === 0
                      ? <div style={{ color: 'var(--epcr-muted, #888)' }}>—</div>
                      : items.map((k, i) => (
                          <div key={i} style={{ padding: '3px 0', borderBottom: '1px solid var(--epcr-border, #e5e7eb)', wordBreak: 'break-all' }}>{k}</div>
                        ))
                    }
                  </div>
                ))}
              </div>
            );
          })()}
        </div>
      </div>
    </div>,
    document.body
  );
}

function formatNoteTimestamp(ts) {
  const d = new Date(ts);
  return isNaN(d.getTime()) ? ts : d.toLocaleString();
}

// Formats a Date as a "YYYY-MM-DDTHH:mm:ss" string in local time, the value
// shape <input type="datetime-local" step="1"> expects.
function toDateTimeLocalValue(d) {
  const pad = n => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

// attachedNotes.source is the raw noteKey used for uid uniqueness ("app:<batchId>" for
// app-submitted batches) — resolve it back to something readable for display only.
function formatAttachedSource(source) {
  if (typeof source === 'string' && source.startsWith('app:')) {
    const batchId = Number(source.slice(4));
    return Number.isFinite(batchId) ? `App (${formatNoteTimestamp(batchId)})` : source;
  }
  return source;
}

// Shared row for one attachable collection of recordings (a static demo file or an
// app-submitted batch) — expand/collapse, "Attach all", and a per-recording checkbox list.
function VoiceNoteSourceRow({ noteKey, label, meta, recordings, isOpen, onToggleOpen, onAttachAll,
                                attachedUidSet, onToggleAttach }) {
  return (
    <div className="epcr-vp-item-wrap">
      <div className="epcr-vp-item" style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', cursor: 'pointer' }} onClick={onToggleOpen}>
        <div className="epcr-vp-item-meta" style={{ flex: 1 }}>
          <span className="epcr-vp-name">{label}</span>
          {meta && <span className="epcr-vp-elem">{meta}</span>}
        </div>
        <button
          className="epcr-mut-btn accept"
          style={{ fontSize: 19, padding: '4px 14px', marginRight: 10 }}
          onClick={e => { e.stopPropagation(); onAttachAll(); }}
        >
          Attach all
        </button>
        <span style={{ color: '#9a9a9a', fontSize: 20 }}>{isOpen ? '▲' : '▼'}</span>
      </div>
      {isOpen && (
        <div style={{ padding: '0 36px 14px' }}>
          {!recordings && <div className="epcr-vp-empty" style={{ padding: '20px 0' }}>Loading…</div>}
          {recordings && recordings.length === 0 && (
            <div className="epcr-vp-empty" style={{ padding: '20px 0' }}>No recordings.</div>
          )}
          {recordings && recordings.map(rec => {
            const uid = `${noteKey}:${rec.id}`;
            const checked = attachedUidSet.has(uid);
            return (
              <label
                key={rec.id}
                style={{ display: 'flex', gap: 12, alignItems: 'flex-start', padding: '10px 0', borderTop: '1px solid #f1f1f1', cursor: 'pointer' }}
              >
                <input type="checkbox" checked={checked} onChange={() => onToggleAttach(rec, noteKey)} style={{ marginTop: 4 }} />
                <div>
                  <div className="epcr-vp-elem">{formatNoteTimestamp(rec.timestamp)}</div>
                  <div className="epcr-vp-msg">{rec.text}</div>
                </div>
              </label>
            );
          })}
        </div>
      )}
    </div>
  );
}

function VoiceNotesPanel({ files, cache, onLoadFile, attachedNotes, onToggleAttach, onAttachAllFromFile,
                            onAddManual, onRemoveAttached, onClearAttached,
                            appBatches = [], onRefreshAppNotes }) {
  const [expandedKey, setExpandedKey]           = React.useState(null);
  const [manualText, setManualText]             = React.useState('');
  const [manualTimestamp, setManualTimestamp]   = React.useState(() => toDateTimeLocalValue(new Date()));

  const attachedUidSet = React.useMemo(() => new Set(attachedNotes.map(n => n.uid)), [attachedNotes]);

  // This panel only mounts while the Voice Notes sub-tab is active (see ValidationPanel),
  // so mount/unmount is already the right signal for when to poll for app-submitted notes.
  React.useEffect(() => {
    onRefreshAppNotes();
    const id = setInterval(onRefreshAppNotes, 5000);
    return () => clearInterval(id);
  }, [onRefreshAppNotes]);

  const handleExpand = (key) => {
    setExpandedKey(prev => (prev === key ? null : key));
  };

  const handleExpandFile = (file) => {
    const wasOpen = expandedKey === file;
    handleExpand(file);
    if (!wasOpen && !cache[file]) onLoadFile(file);
  };

  const handleAttachAllFile = async (file) => {
    const recordings = cache[file] || (await onLoadFile(file));
    onAttachAllFromFile(file, recordings);
  };

  const handleAdd = () => {
    const text = manualText.trim();
    if (!text) return;
    const iso = manualTimestamp ? new Date(manualTimestamp).toISOString() : new Date().toISOString();
    onAddManual(text, iso);
    setManualText('');
    // manualTimestamp is intentionally left as-is so consecutive notes default
    // to the same time unless the user changes it.
  };

  return (
    <>
      {attachedNotes.length > 0 && (
        <>
          <div className="epcr-vp-group-head" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span>Attached to this send ({attachedNotes.length})</span>
            <button
              className="epcr-mut-btn reject"
              style={{ padding: '4px 14px', fontSize: 19 }}
              onClick={onClearAttached}
            >
              Remove all
            </button>
          </div>
          {attachedNotes.map(n => (
            <div key={n.uid} className="epcr-vp-item" style={{ cursor: 'default' }}>
              <div className="epcr-vp-item-meta">
                <span className="epcr-vp-elem">{formatNoteTimestamp(n.timestamp)}</span>
                <span className="epcr-vp-name" style={{ fontSize: 20, fontWeight: 500, color: '#9a9a9a' }}>
                  {formatAttachedSource(n.source)}
                </span>
                <button
                  className="epcr-mut-btn reject"
                  style={{ marginLeft: 'auto', padding: '4px 14px', fontSize: 20 }}
                  onClick={() => onRemoveAttached(n.uid)}
                >
                  Remove
                </button>
              </div>
              <div className="epcr-vp-msg">{n.text}</div>
            </div>
          ))}
        </>
      )}

      <div className="epcr-vp-group-head" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span>From app{appBatches.length > 0 ? ` (${appBatches.length})` : ''}</span>
        <button
          className="epcr-mut-btn accept"
          style={{ padding: '4px 14px', fontSize: 19 }}
          onClick={onRefreshAppNotes}
        >
          Refresh
        </button>
      </div>
      {appBatches.length === 0 && (
        <div className="epcr-vp-empty" style={{ padding: '14px 36px' }}>No notes received from the app yet.</div>
      )}
      {appBatches.map(batch => {
        const key = `app:${batch.batchId}`;
        return (
          <VoiceNoteSourceRow
            key={key}
            noteKey={key}
            label={`From App — ${formatNoteTimestamp(batch.receivedAt)}`}
            meta={`${batch.count} recording${batch.count !== 1 ? 's' : ''}`}
            recordings={batch.recordings}
            isOpen={expandedKey === key}
            onToggleOpen={() => handleExpand(key)}
            onAttachAll={() => onAttachAllFromFile(key, batch.recordings)}
            attachedUidSet={attachedUidSet}
            onToggleAttach={onToggleAttach}
          />
        );
      })}

      <div className="epcr-vp-group-head">Voice note files</div>
      {(files || []).map(file => (
        <VoiceNoteSourceRow
          key={file}
          noteKey={file}
          label={file}
          meta={cache[file] ? `${cache[file].length} recording${cache[file].length !== 1 ? 's' : ''}` : null}
          recordings={cache[file]}
          isOpen={expandedKey === file}
          onToggleOpen={() => handleExpandFile(file)}
          onAttachAll={() => handleAttachAllFile(file)}
          attachedUidSet={attachedUidSet}
          onToggleAttach={onToggleAttach}
        />
      ))}

      <div className="epcr-vp-group-head">New voice note (this session only)</div>
      <div style={{ padding: '14px 36px', display: 'flex', flexDirection: 'column', gap: 10 }}>
        <div className="epcr-vp-msg" style={{ color: '#9a9a9a', fontSize: 20 }}>
          Not saved to any file — only attached for this browser session.
        </div>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4, alignSelf: 'flex-start' }}>
          <span style={{ fontSize: 19, color: '#9a9a9a' }}>Timestamp</span>
          <input
            type="datetime-local"
            step="1"
            value={manualTimestamp}
            onChange={e => setManualTimestamp(e.target.value)}
            style={{ fontFamily: 'inherit', fontSize: 20, padding: 8, border: '1.5px solid #d4d4d4', borderRadius: 6 }}
          />
        </label>
        <textarea
          value={manualText}
          onChange={e => setManualText(e.target.value)}
          placeholder="Transcribed voice note text…"
          rows={3}
          style={{ width: '100%', fontFamily: 'inherit', fontSize: 22, padding: 10, boxSizing: 'border-box', border: '1.5px solid #d4d4d4', borderRadius: 6, resize: 'vertical' }}
        />
        <button
          className="epcr-mut-btn accept"
          style={{ alignSelf: 'flex-start', opacity: manualText.trim() ? 1 : 0.5 }}
          onClick={handleAdd}
          disabled={!manualText.trim()}
        >
          Add note
        </button>
      </div>
    </>
  );
}

function ViolationItem({ v, onNavigate, specFn }) {
  const isFatal  = v.role === '[FATAL]';
  const spec     = v.element ? specFn(v.element) : null;
  const elemName = spec?.name || '';

  return (
    <div className="epcr-vp-item-wrap">
      <button className="epcr-vp-item" onClick={() => onNavigate(v)}>
        <div className="epcr-vp-item-meta">
          <span className={"epcr-vp-badge " + (isFatal ? "fatal" : "warn")}>
            {isFatal ? "Fatal" : "Warning"}
          </span>
          {v.element && <span className="epcr-vp-elem">{v.element}</span>}
          {elemName && <span className="epcr-vp-name">{elemName}</span>}
        </div>
        <div className="epcr-vp-msg">{v.message}</div>
      </button>
      {v.contextXml && (
        <details className="epcr-vp-ctx">
          <summary>context</summary>
          {v.contextLocalName != null && (
            <div className="epcr-vp-ctx-row">
              <span className="epcr-vp-ctx-label">node:</span>
              <span className="epcr-vp-ctx-val">{v.contextLocalName}</span>
            </div>
          )}
          {v.contextGroupIndex != null && (
            <div className="epcr-vp-ctx-row">
              <span className="epcr-vp-ctx-label">groupIndex:</span>
              <span className="epcr-vp-ctx-val">{v.contextGroupIndex}</span>
            </div>
          )}
          <pre className="epcr-vp-ctx-xml">{v.contextXml}</pre>
        </details>
      )}
    </div>
  );
}

const FEEDBACK_TARGET_LABELS = {
  'missing-vital': 'Missing Vital',
  'missing-med': 'Missing Medication',
  'missing-procedure': 'Missing Procedure',
  'vitals-discrepancy': 'Vitals Discrepancy',
  'medications-discrepancy': 'Medications Discrepancy',
  'procedure-discrepancy': 'Procedure Discrepancy',
  'schematron': 'Schematron',
};

function DropdownResultItem({ r }) {
  return (
    <div className="epcr-vp-item" style={{ cursor: 'default' }}>
      <div className="epcr-vp-item-meta">
        <span className="epcr-vp-badge warn">
          {FEEDBACK_TARGET_LABELS[r.feedbackTarget] || r.feedbackTarget}
        </span>
        {r.validated && (
          <span className="epcr-vp-badge" style={{ background: '#eef9f0', color: '#16a34a' }}>Validated</span>
        )}
        {r.test_metadata_id && (
          <span className="epcr-vp-elem" style={{ marginLeft: 'auto' }}>#{r.test_metadata_id}</span>
        )}
      </div>
      <div className="epcr-vp-msg" style={{ fontWeight: 500 }}>{r.feedbackText}</div>
      {r.feedbackJustification && (
        <div className="epcr-vp-msg" style={{ color: '#6e6e6e', fontSize: '22px' }}>{r.feedbackJustification}</div>
      )}
    </div>
  );
}

// Mirrors the backend's GROUP_IDENTITY (gpt-controller.js) — the repeating group's
// catalog-backed identifying field, used to resolve a human-readable name for the
// specific instance a mutation targets (e.g. "Medication #2 — Oral Glucose" instead
// of a bare index).
const GROUP_IDENTITY_ELEM = {
  'eMedications.MedicationGroup': 'eMedications.03',
  'eProcedures.ProcedureGroup':   'eProcedures.03',
};

const OP_META = {
  add_group:      { label: 'Add Group',   bg: 'var(--epcr-blue-soft)', color: 'var(--epcr-blue)' },
  add_group_full: { label: 'Full Add',    bg: 'var(--epcr-blue-soft)', color: 'var(--epcr-blue)' },
  set:            { label: 'Set',         bg: 'var(--epcr-blue-soft)', color: 'var(--epcr-blue)' },
  replace:        { label: 'Replace',     bg: '#fff8e1',               color: '#8a6f00' },
  clear:          { label: 'Clear',       bg: 'var(--epcr-red-soft)',  color: 'var(--epcr-red)' },
  ask_user:       { label: 'Needs Input', bg: 'var(--epcr-blue-soft)', color: 'var(--epcr-blue)' },
};

function MutationCard({ r, isAccepted, onAccept, onReject }) {
  const data = useData();
  const op = OP_META[r.op] || null;
  const isActionable = r.op !== null;
  const isFatal = r.severity === '[FATAL]';
  const isAskUser = r.op === 'ask_user';
  const isRichAdd = r.op === 'add_group_full';

  // Local-only draft values for a rich add's optional fill-in fields — there's no
  // report path to bind to yet (the group doesn't exist until Accept), unlike
  // ask_user's live-path fields below. Merged into r.template only on Accept.
  const [draftFields, setDraftFields] = React.useState({});

  // Resolved unconditionally (cheap, side-effect free) so the useField() hook below
  // always runs in the same order regardless of op/accepted state.
  const m = promoteToMutation(r, data);
  const hasGroupIndex = Number.isInteger(r.groupIndex) && r.groupIndex >= 0;
  const askUserPath = isAskUser ? (m.path || '') : '';
  const askUserValue = useField(askUserPath);
  const hasAskUserValue = Array.isArray(askUserValue)
    ? askUserValue.length > 0
    : (askUserValue !== null && askUserValue !== undefined && askUserValue !== '');

  // Identify which member of a repeating group this card targets — e.g. "Medication
  // #2 — Oral Glucose" — so the user (and, separately, the LLM in enrichSchematron-
  // Violations) never has to guess which instance is actually being discussed.
  const groupNumber = m.groupPath ? m.groupPath.split('/').pop() : null;
  const idElem = groupNumber ? GROUP_IDENTITY_ELEM[groupNumber] : null;
  const idPath = (hasGroupIndex && idElem)
    ? (promoteToMutation({ elem: idElem, groupIndex: r.groupIndex }, data).path || '')
    : '';
  const idValue = useField(idPath);
  const idLabel = (idElem && idValue != null && idValue !== '') ? data.label(idElem, idValue) : null;
  const groupHeaderLabel = groupNumber ? groupLabel(groupNumber) : null;

  // Reject on an ask_user card must leave no trace in the report, matching every
  // other card type — if the user had started answering, clear it back out first.
  const handleRejectAskUser = () => {
    if (hasAskUserValue && askUserPath) {
      data.setReportPath(askUserPath, Array.isArray(askUserValue) ? [] : "");
    }
    onReject(r);
  };

  // Only merge draft values the user actually filled in — a field opened and then
  // cleared back out should end up absent, same as never having been touched at all.
  const mergedRichAddTemplate = () => ({
    ...r.template,
    ...Object.fromEntries(
      Object.entries(draftFields).filter(([, v]) => v !== '' && v != null && !(Array.isArray(v) && v.length === 0))
    ),
  });

  // Human-readable display for a rich add's known-fields preview — resolves coded
  // values (including multi-select arrays) via data.label(), and lightly formats
  // dateTime values instead of showing the full ISO-8601 offset string.
  const formatKnownValue = (elem, value) => {
    if (Array.isArray(value)) return value.map(v => data.label(elem, v)).join(', ');
    const spec = data.spec(elem);
    if ((spec?.baseType === 'dateTime' || spec?.baseType === 'date') && typeof value === 'string') {
      return value.substring(0, 16).replace('T', ' ');
    }
    return data.label(elem, value);
  };

  if (isAccepted) {
    return (
      <div className="epcr-mut-accepted-row">
        <span className="epcr-mut-check">✓</span>
        <span className="epcr-mut-accepted-label">{r.display || r.feedbackText}</span>
        {r.elem && <span className="epcr-vp-elem">{r.elem}</span>}
      </div>
    );
  }

  return (
    <div className="epcr-vp-item epcr-mut-card" style={{ cursor: 'default' }}>
      <div className="epcr-vp-item-meta">
        {op && (
          <span className="epcr-vp-badge" style={{ background: op.bg, color: op.color }}>
            {op.label}
          </span>
        )}
        <span className="epcr-vp-badge warn">
          {FEEDBACK_TARGET_LABELS[r.feedbackTarget] || r.feedbackTarget}
        </span>
        {r.severity && (
          <span className={"epcr-vp-badge " + (isFatal ? 'fatal' : 'warn')}>
            {isFatal ? 'Fatal' : 'Warning'}
          </span>
        )}
        {r.test_metadata_id && (
          <span className="epcr-vp-elem" style={{ marginLeft: 'auto' }}>#{r.test_metadata_id}</span>
        )}
      </div>

      {r.display && (
        <div className="epcr-vp-msg" style={{ fontWeight: 600, fontSize: '26px' }}>{r.display}</div>
      )}
      {r.feedbackText && (
        <div className="epcr-vp-msg" style={{ fontWeight: 500 }}>{r.feedbackText}</div>
      )}
      {r.feedbackJustification && (
        <div className="epcr-vp-msg" style={{ color: '#6e6e6e', fontSize: '22px' }}>{r.feedbackJustification}</div>
      )}

      {hasGroupIndex && groupHeaderLabel && (
        <div className="epcr-mut-group-tag">
          {groupHeaderLabel} #{r.groupIndex + 1}{idLabel ? ` — ${idLabel}` : ''}
        </div>
      )}

      {isAskUser ? (
        <div className="epcr-mut-ask-field epcr-root">
          <RawField elem={r.elem} path={hasGroupIndex ? m.path : undefined} />
        </div>
      ) : isRichAdd ? (
        <>
          <div className="epcr-mut-fields">
            {Object.entries(r.template || {}).map(([elem, val]) => (
              <span className="epcr-mut-field" key={elem}>
                <span className="epcr-mut-field-label">{data.spec(elem)?.name || elem}</span>
                <span className="epcr-vp-elem">{formatKnownValue(elem, val)}</span>
              </span>
            ))}
          </div>
          {r.missingFields && r.missingFields.length > 0 && (
            <>
              <div className="epcr-mut-field-label" style={{ marginTop: 8 }}>
                Optional — fill in now, or answer later
              </div>
              <div className="epcr-mut-ask-field epcr-root">
                {r.missingFields.map(elem => (
                  <AutoField
                    key={elem}
                    elem={elem}
                    label={data.spec(elem)?.name || elem}
                    value={draftFields[elem]}
                    onChange={v => setDraftFields(f => ({ ...f, [elem]: v }))}
                  />
                ))}
              </div>
            </>
          )}
        </>
      ) : (
        <div className="epcr-mut-fields">
          {r.elem && (
            <span className="epcr-mut-field">
              <span className="epcr-mut-field-label">elem</span>
              <span className="epcr-vp-elem">{r.elem}</span>
            </span>
          )}
          {r.value !== null && r.value !== undefined && (
            <span className="epcr-mut-field">
              <span className="epcr-mut-field-label">value</span>
              <span className="epcr-vp-elem">{String(r.value)}</span>
            </span>
          )}
        </div>
      )}

      {isActionable ? (
        <div className="epcr-mut-actions">
          <button
            className={"epcr-mut-btn accept" + (isFatal ? " fatal-accept" : "")}
            onClick={() => onAccept(
              isRichAdd ? { ...r, template: mergedRichAddTemplate() } : r
            )}
            disabled={isAskUser && !hasAskUserValue}
          >
            Accept
          </button>
          <button
            className="epcr-mut-btn reject"
            onClick={isAskUser ? handleRejectAskUser : () => onReject(r)}
          >
            Reject
          </button>
        </div>
      ) : (
        <div className="epcr-mut-manual">Requires manual review</div>
      )}
    </div>
  );
}
